import type { CSSProperties } from 'react';
import {
  BellRing,
  CircleAlert,
  CircleCheck,
  LoaderCircle,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Toaster as Sonner } from 'sonner';

import { cn } from '@/lib/utils';

/**
 * The short confirmations: "Delivered. Nice one.", "New job for you".
 *
 * Positioned at the TOP, unlike most apps, and just under the header. The
 * bottom of this screen is the pinned primary action and, on the sheets, the
 * keyboard — a toast there would land over the one control the rider is
 * reaching for.
 *
 * `unstyled`, and drawn entirely from the tokens. Sonner's own look is written
 * OUTSIDE any cascade layer, and Tailwind v4 puts every utility inside
 * `@layer utilities` — an unlayered rule beats a layered one whatever its
 * specificity, so classes passed alongside sonner's styling were silently
 * ignored. Unstyled, sonner keeps only the positioning and the stacking.
 *
 * Each tone is its own SURFACE, not a neutral card with a coloured dot: a
 * toast has to be told apart from the job cards under it at a glance, and a
 * red-washed "Finish the order you are carrying first" reads as a refusal
 * before a word of it is read. The solid disc carries the icon, so the tone is
 * never colour alone — a green disc and a red one are the same disc in
 * sunlight, and the tick and the exclamation are not.
 *
 * As wide as the app column, so it lines up with the cards rather than
 * floating narrower than them.
 *
 * A toast may NOT outrun the truth. Every call site fires one only after the
 * server has answered, never on the tap.
 */
export function Toaster() {
  const offset = 'calc(var(--layout-header) + var(--space-2))';

  return (
    <Sonner
      position="top-center"
      // Long enough to read while walking, short enough not to sit over the
      // status pill it is confirming.
      duration={3200}
      gap={8}
      closeButton
      style={
        {
          '--width': 'calc(var(--layout-container) - var(--layout-gutter) * 2)',
        } as CSSProperties
      }
      offset={{ top: offset }}
      mobileOffset={{ top: offset, left: 'var(--layout-gutter)', right: 'var(--layout-gutter)' }}
      icons={{
        success: <ToneDisc icon={CircleCheck} />,
        error: <ToneDisc icon={CircleAlert} />,
        warning: <ToneDisc icon={TriangleAlert} />,
        info: <ToneDisc icon={BellRing} />,
        loading: <ToneDisc icon={LoaderCircle} spin />,
        close: <X className="size-4" aria-hidden="true" />,
      }}
      toastOptions={{
        unstyled: true,
        closeButtonAriaLabel: 'Dismiss',
        classNames: {
          /*
           * No surface or border colour here — each tone below sets its own.
           * Put a neutral one on the base as well and it competes with the
           * tone's on equal footing, and Tailwind orders same-property
           * utilities by NAME: `border-line-bold` beat `border-error-line`
           * but lost to `border-success-line`, so half the tones came out
           * grey.
           */
          toast: cn(
            'group relative flex w-(--width) items-start gap-3 rounded-2xl border-2',
            'p-3.5 pe-11 text-ink shadow-xl',
          ),
          title: 'text-base font-bold leading-snug tracking-tight',
          description: 'mt-0.5 text-sm leading-snug text-ink-muted',
          content: 'min-w-0 flex-1 self-center',
          icon: 'shrink-0',
          actionButton: cn(
            'mt-0.5 shrink-0 self-center rounded-lg bg-primary px-3.5 text-sm font-bold text-on-primary',
            'h-9 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
          ),
          cancelButton: 'shrink-0 self-center rounded-lg px-3 h-9 text-sm font-semibold text-ink-muted',
          closeButton: cn(
            'absolute end-2 top-2 grid size-8 place-items-center rounded-full text-ink-muted',
            'hover:bg-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
          ),
          /*
           * The disc reverses the tone's ink and surface — the same pair the
           * contrast gate already checks, so it cannot fail it.
           */
          success:
            'border-success-line bg-success-surface [&_[data-tone-disc]]:bg-success-ink [&_[data-tone-disc]]:text-success-surface',
          error:
            'border-error-line bg-error-surface [&_[data-tone-disc]]:bg-error-ink [&_[data-tone-disc]]:text-error-surface',
          warning:
            'border-warning-line bg-warning-surface [&_[data-tone-disc]]:bg-warning-ink [&_[data-tone-disc]]:text-warning-surface',
          info: 'border-info-line bg-info-surface [&_[data-tone-disc]]:bg-info-ink [&_[data-tone-disc]]:text-info-surface',
          default: 'border-line-bold bg-raised',
          loading:
            'border-line-bold bg-raised [&_[data-tone-disc]]:bg-muted [&_[data-tone-disc]]:text-ink-muted',
        },
      }}
    />
  );
}

function ToneDisc({ icon: Icon, spin = false }: { icon: LucideIcon; spin?: boolean }) {
  return (
    <span data-tone-disc className="grid size-10 place-items-center rounded-full">
      <Icon
        className={cn('size-5', spin && 'animate-spin motion-reduce:animate-none')}
        strokeWidth={2.5}
        aria-hidden="true"
      />
    </span>
  );
}
